import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { getKPIPayrollMatches, pushKPIToPayroll } from '../../services/api';
import { fmtScore, fmtValue, money, MONTHS, periodLabel } from './kpiFormat';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');

/* Ведомость премий для бухгалтера. Пока месяц не закрыт, это черновик —
   об этом сказано и на экране, и в выгрузке, чтобы предварительные цифры
   не ушли в выплату. */
const KpiAccounting = ({ data, period, notify, onGoTab, onReload }) => {
  const [open, setOpen] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const closed = data.status === 'closed';

  const roleIndex = Object.fromEntries(data.roles.map((r, i) => [r.key, i]));
  const roleByKey = Object.fromEntries(data.roles.map(r => [r.key, r]));
  const rows = useMemo(() => [...data.employees].sort((a, b) =>
    (roleIndex[a.role] - roleIndex[b.role]) || a.name.localeCompare(b.name, 'ru')),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data.employees]);
  const total = rows.reduce((s, e) => s + e.result.bonus, 0);
  const byRole = data.roles.map(r => ({
    name: r.name,
    count: rows.filter(e => e.role === r.key).length,
    sum: rows.filter(e => e.role === r.key).reduce((s, e) => s + e.result.bonus, 0),
  })).filter(r => r.count);

  const metricName = (role, key) => roleByKey[role]?.metrics.find(m => m.key === key);
  const statusText = closed
    ? `Утверждено ${new Date(data.closed_at).toLocaleDateString('ru-RU')}${data.closed_by_name ? `, ${data.closed_by_name}` : ''}`
    : 'ЧЕРНОВИК — месяц не закрыт, суммы могут измениться';
  const title = `Ведомость премий KPI — ${periodLabel(period.year, period.month).toLowerCase()}`;

  const exportXlsx = () => {
    const sheet1 = [
      [title], [statusText], [],
      ['№', 'Сотрудник', 'Должность', 'Итоговый балл', 'Премия, ₽', 'Примечание'],
      ...rows.map((e, i) => [
        i + 1, e.name, roleByKey[e.role]?.name, e.result.score ?? '',
        Math.round(e.result.bonus),
        e.result.complete ? (e.result.bonus ? '' : `балл ниже ${data.min_bonus_score}`) : 'не заполнен факт',
      ]),
      [],
      ['', 'ИТОГО', '', '', Math.round(total), ''],
    ];
    const sheet2 = [
      ['Сотрудник', 'Должность', 'Показатель', 'Вес', 'Факт', 'Балл', 'Вес × балл'],
      ...rows.flatMap(e => [
        ...(roleByKey[e.role]?.has_revenue ? [[e.name, roleByKey[e.role].name, 'Личная выручка (база премии)', '', fmtValue('rub', e.revenue), '', '']] : []),
        ...e.result.rows.map(r => {
          const m = metricName(e.role, r.key);
          return [e.name, roleByKey[e.role]?.name, m?.name || r.key, `${Math.round(r.weight * 100)}%`,
            m ? fmtValue(m.unit, r.value) : r.value, r.score ?? '', r.weighted ?? ''];
        }),
        [e.name, roleByKey[e.role]?.name, 'ИТОГОВЫЙ БАЛЛ', '', '', '', e.result.score ?? ''],
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet(sheet1);
    ws1['!cols'] = [{ wch: 5 }, { wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 12 }, { wch: 22 }];
    const ws2 = XLSX.utils.aoa_to_sheet(sheet2);
    ws2['!cols'] = [{ wch: 26 }, { wch: 16 }, { wch: 40 }, { wch: 7 }, { wch: 16 }, { wch: 7 }, { wch: 11 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Ведомость');
    XLSX.utils.book_append_sheet(wb, ws2, 'Расшифровка');
    XLSX.writeFile(wb, `KPI_premii_${period.year}-${String(period.month).padStart(2, '0')}${closed ? '' : '_chernovik'}.xlsx`);
  };

  const print = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${esc(title)}</title>
      <style>body{font:13px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:28px}
      h1{font-size:18px;margin:0 0 4px}.st{color:${closed ? '#555' : '#b91c1c'};margin-bottom:16px;font-weight:600}
      table{border-collapse:collapse;width:100%}th,td{border:1px solid #bbb;padding:6px 8px;text-align:left}
      th{background:#f1f1f1}td.n{text-align:right;white-space:nowrap}tfoot td{font-weight:700}
      .sign{margin-top:36px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px}.sign div{border-top:1px solid #333;padding-top:4px;font-size:12px;color:#444}</style>
      </head><body><h1>${esc(title)}</h1><div class="st">${esc(statusText)}</div>
      <table><thead><tr><th>№</th><th>Сотрудник</th><th>Должность</th><th>Балл</th><th>Премия</th></tr></thead><tbody>
      ${rows.map((e, i) => `<tr><td>${i + 1}</td><td>${esc(e.name)}</td><td>${esc(roleByKey[e.role]?.name)}</td>
        <td class="n">${esc(fmtScore(e.result.score))}</td><td class="n">${esc(money(e.result.bonus))}</td></tr>`).join('')}
      </tbody><tfoot><tr><td colspan="4">Итого</td><td class="n">${esc(money(total))}</td></tr></tfoot></table>
      <div class="sign"><div>Составил (управляющий)</div><div>Утвердил (руководитель)</div><div>Принял (бухгалтер)</div></div>
      <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="kpi2-accounting">
      <div className={`kpi2-note ${closed ? 'is-ok' : 'is-warn'}`}>
        {closed ? (
          <>
            Ведомость утверждена {new Date(data.closed_at).toLocaleDateString('ru-RU')}
            {data.closed_by_name ? `, ${data.closed_by_name}` : ''}. Суммы зафиксированы и больше не меняются.
          </>
        ) : (
          <>
            Это черновик: факт ещё вносится, суммы предварительные. Когда всё заполнено, нажмите
            «Закрыть месяц» вверху страницы — ведомость станет утверждённой.
          </>
        )}
      </div>

      <div className="kpi2-tiles">
        <div className="kpi2-tile kpi2-tile-accent">
          <span className="kpi2-tile-label">К выплате за {MONTHS[period.month - 1].toLowerCase()}</span>
          <strong>{money(total)}</strong>
          <span className="kpi2-tile-sub">{rows.filter(e => e.result.bonus > 0).length} сотрудников с премией</span>
        </div>
        {byRole.map(r => (
          <div key={r.name} className="kpi2-tile">
            <span className="kpi2-tile-label">{r.name}</span>
            <strong>{money(r.sum)}</strong>
            <span className="kpi2-tile-sub">{r.count} чел.</span>
          </div>
        ))}
      </div>

      <section className="kpi2-card">
        <div className="kpi2-card-head kpi2-card-head-row">
          <div>
            <h3>Ведомость премий</h3>
            <p>Нажмите на строку — откроется расшифровка: факт, балл и вес по каждому показателю.</p>
          </div>
          <div className="kpi2-role-actions">
            <button type="button" className="ui-btn-ghost" onClick={print}>Печать</button>
            <button type="button" className="ui-btn-ghost" onClick={exportXlsx}>Скачать Excel</button>
            <button type="button" className="ui-btn-primary" disabled={!closed}
              title={closed ? undefined : 'Сначала закройте месяц'}
              onClick={() => setPayroll({ loading: true })}>Передать в табель</button>
          </div>
        </div>

        <div className="kpi2-ledger" role="table">
          <div className="kpi2-ledger-row is-head" role="row">
            <span role="columnheader">№</span>
            <span role="columnheader">Сотрудник</span>
            <span role="columnheader">Должность</span>
            <span role="columnheader">Балл</span>
            <span role="columnheader">Премия</span>
          </div>
          {rows.map((e, i) => (
            <React.Fragment key={e.id}>
              <button type="button" className={`kpi2-ledger-row ${open === e.id ? 'is-open' : ''}`} role="row"
                onClick={() => setOpen(open === e.id ? null : e.id)} aria-expanded={open === e.id}>
                <span role="cell">{i + 1}</span>
                <span role="cell" className="kpi2-ledger-name">{e.name}</span>
                <span role="cell" className="kpi2-muted">{roleByKey[e.role]?.name}</span>
                <span role="cell">{e.result.complete ? fmtScore(e.result.score) : <span className="ui-text-danger">не заполнено</span>}</span>
                <span role="cell" className="kpi2-ledger-sum">{money(e.result.bonus)}</span>
              </button>
              {open === e.id && (
                <div className="kpi2-ledger-detail">
                  {roleByKey[e.role]?.has_revenue && (
                    <div>Личная выручка (база премии): <strong>{money(e.revenue)}</strong></div>
                  )}
                  {e.result.rows.map(r => {
                    const m = metricName(e.role, r.key);
                    return (
                      <div key={r.key}>
                        {m?.name}: факт <strong>{m ? fmtValue(m.unit, r.value) : r.value}</strong>
                        {r.score !== null && <> → {r.score} б. × {Math.round(r.weight * 100)}% = {r.weighted.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}</>}
                      </div>
                    );
                  })}
                  <div className="kpi2-ledger-total">
                    Итоговый балл {fmtScore(e.result.score)} → премия {money(e.result.bonus)}
                    {e.result.complete && !e.result.bonus && ` (ниже ${data.min_bonus_score} баллов премия не начисляется)`}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          <div className="kpi2-ledger-row is-total" role="row">
            <span role="cell" />
            <span role="cell">Итого</span>
            <span role="cell" />
            <span role="cell" />
            <span role="cell" className="kpi2-ledger-sum">{money(total)}</span>
          </div>
        </div>
        {data.payroll_pushed_at && (
          <p className="kpi2-muted">Премии переданы в табель «Сотрудников» {new Date(data.payroll_pushed_at).toLocaleString('ru-RU')}.</p>
        )}
        {rows.length === 0 && (
          <div className="kpi2-empty">
            В этом месяце нет сотрудников в KPI.
            <button type="button" className="ui-btn-ghost" onClick={() => onGoTab('roles')}>Добавить</button>
          </div>
        )}
      </section>

      {payroll && (
        <PayrollModal period={period} employees={rows} roleByKey={roleByKey} notify={notify}
          onClose={() => { setPayroll(null); onReload(); }} />
      )}
    </div>
  );
};

/* Передача премий в табель: показываем, кому из «Сотрудников» уйдёт
   каждая премия, несопоставленных — выбрать руками или пропустить. */
const PayrollModal = ({ period, employees, roleByKey, notify, onClose }) => {
  const [state, setState] = useState({ loading: true });
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    getKPIPayrollMatches(period.year, period.month)
      .then(r => {
        const m = {};
        employees.forEach(e => {
          const c = r.matches[e.id] || [];
          m[e.id] = c.length === 1 ? String(c[0].id) : '';
        });
        setMapping(m);
        setState({ staff: r.staff, matches: r.matches });
      })
      .catch(() => setState({ error: 'Не удалось получить список сотрудников' }));
  }, [period, employees]);

  const push = async () => {
    setBusy(true);
    try {
      const sel = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      const res = await pushKPIToPayroll(period.year, period.month, sel);
      setResult(res);
      notify('ok', `Премии переданы в табель: ${res.done.length}`);
    } catch (e) {
      notify('err', e.response?.data?.error || 'Не удалось передать премии');
    } finally {
      setBusy(false);
    }
  };

  const unmapped = employees.filter(e => !mapping[e.id]).length;

  return (
    <div className="ui-modal" onClick={onClose}>
      <div className="ui-modal-box kpi2-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="ui-modal-head">
          <h2>Премии → табель «Сотрудников»</h2>
          <button className="ui-icon-btn" onClick={onClose} aria-label="Закрыть">×</button>
        </div>
        {state.loading && <p className="kpi2-muted">Сопоставляем сотрудников…</p>}
        {state.error && <div className="ui-inline-error">{state.error}</div>}
        {state.staff && !result && (
          <>
            <p className="kpi2-muted">
              Премия запишется в поле «Премия» табеля за {MONTHS[period.month - 1].toLowerCase()} и войдёт в итог к выплате.
              Сотрудник сопоставлен по ФИО; если не нашёлся — выберите вручную или оставьте пустым, тогда его премию
              бухгалтер внесёт сам.
            </p>
            <div className="kpi2-map">
              {employees.map(e => (
                <div key={e.id} className={`kpi2-map-row ${mapping[e.id] ? '' : 'is-unmapped'}`}>
                  <span>
                    <strong>{e.name}</strong>
                    <span className="kpi2-muted"> · {roleByKey[e.role]?.name} · {money(e.result.bonus)}</span>
                  </span>
                  <select value={mapping[e.id] || ''} onChange={ev => setMapping(m => ({ ...m, [e.id]: ev.target.value }))}
                    aria-label={`Сотрудник в табеле для ${e.name}`}>
                    <option value="">— не передавать —</option>
                    {state.staff.map(s => (
                      <option key={s.id} value={s.id}>{s.name}{s.status !== 'active' ? ' (уволен)' : ''}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="ui-modal-actions">
              {unmapped > 0 && <span className="kpi2-muted kpi2-grow">Не будет передано: {unmapped}</span>}
              <button className="ui-btn-ghost" onClick={onClose}>Отмена</button>
              <button className="ui-btn-primary kpi2-btn-auto" onClick={push} disabled={busy}>
                {busy ? 'Передаём…' : 'Передать'}
              </button>
            </div>
          </>
        )}
        {result && (
          <>
            <p>Записано в табель за {result.period}: <strong>{result.done.length}</strong>.</p>
            {result.problems.length > 0 && (
              <div className="kpi2-note is-warn">
                Не переданы: {result.problems.map(p => p.name).join(', ')}. Их премию нужно внести в табель вручную.
              </div>
            )}
            <div className="ui-modal-actions">
              <button className="ui-btn-primary kpi2-btn-auto" onClick={onClose}>Готово</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default KpiAccounting;
